import React, { useState } from 'react';
import { isAgentLoggedIn } from '../../utils/auth';
import containerAPI from '../../services/containerAPI';
import customerAPI from '../../services/customerAPI';
import { handleApiError } from '../../utils/helpers';
import { validateContainerNumber, calculateCheckDigit } from '../../utils/ContainerNumberValidator';
import TrackingTimeline from './TrackingTimeline';
import LoadingSpinner from '../common/LoadingSpinner';
import './CustomerPortal.css';

const CustomerPortal = () => {
  const [searchData, setSearchData] = useState({
    containerNumber: '',
    bookingNumber: ''
  });
  const [trackingData, setTrackingData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSearch = async (e) => {
    e.preventDefault();

    if (!searchData.containerNumber && !searchData.bookingNumber) {
      setError('Please enter either container number or booking number');
      return;
    }

    if (searchData.containerNumber && !validateContainerNumber(searchData.containerNumber)) {
      setError('Invalid container number format or check digit.');
      return;
    }

    setLoading(true);
    setError('');
    setTrackingData(null);

    try {
      let response;
      if (isAgentLoggedIn()) {
        // Authenticated: use containerAPI.getByNumber
        response = await containerAPI.getByNumber(searchData.containerNumber);
      } else {
        // Public: use customerAPI.track
        response = await customerAPI.track(
          searchData.containerNumber,
          searchData.bookingNumber
        );
      }
      setTrackingData(response.data);
    } catch (error) {
      setError(handleApiError(error));
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    setSearchData({
      ...searchData,
      [e.target.name]: e.target.value.toUpperCase()
    });
  };

  const resetSearch = () => {
    setSearchData({ containerNumber: '', bookingNumber: '' });
    setTrackingData(null);
    setError('');
  };

  return (
    <div className="customer-portal">
      <div className="search-section">
        <div className="search-card">
          <h2>Track Your Shipment</h2>
          <p>Enter your container number or booking number to track your shipment</p>

          <form onSubmit={handleSearch} className="search-form">
            <div className="input-group">
              <div className="form-group">
                <label htmlFor="containerNumber">Container Number</label>
                <input
                  type="text"
                  id="containerNumber"
                  name="containerNumber"
                  value={searchData.containerNumber}
                  onChange={handleInputChange}
                  placeholder="e.g., CMAU1234567"
                  disabled={loading}
                />
              </div>

              <div className="form-divider">
                <span>OR</span>
              </div>

              <div className="form-group">
                <label htmlFor="bookingNumber">Booking Number</label>
                <input
                  type="text"
                  id="bookingNumber"
                  name="bookingNumber"
                  value={searchData.bookingNumber}
                  onChange={handleInputChange}
                  placeholder="e.g., SHIP-6482"
                  disabled={loading}
                />
              </div>
            </div>

            {error && (
              <div className="alert alert-error">
                {error}
              </div>
            )}

            <div className="search-actions">
              <button 
                type="submit" 
                className="btn btn-primary search-btn"
                disabled={loading}
              >
                {loading ? <LoadingSpinner size="small" text="" /> : 'Track Shipment'}
              </button>
              
              {trackingData && (
                <button 
                  type="button" 
                  className="btn btn-secondary"
                  onClick={resetSearch}
                >
                  New Search
                </button>
              )}
            </div>
          </form>
        </div>
      </div>

      {loading && (
        <div className="loading-section">
          <LoadingSpinner text="Searching for your shipment..." />
        </div>
      )}

      {trackingData && (
        <div className="tracking-results">
          <div className="shipment-summary">
            <div className="summary-header">
              <h2>Shipment Details</h2>
              <div className="status-badge large">
                {trackingData.currentStatus}
              </div>
            </div>

            <div className="summary-grid">
              <div className="summary-item">
                <label>Container Number</label>
                <span className="value highlight">
                  {trackingData.container?.containerNumber || 'N/A'}
                </span>
              </div>
              
              <div className="summary-item">
                <label>Booking Number</label>
                <span className="value">
                  {trackingData.container?.bookingNumber || 'N/A'}
                </span>
              </div>
              
              <div className="summary-item">
                <label>Current Status</label>
                <span className="value status">
                  {trackingData.currentStatus}
                </span>
              </div>
              
              <div className="summary-item">
                <label>Current Location</label>
                <span className="value">
                  {trackingData.currentLocation}
                </span>
              </div>
              
              <div className="summary-item">
                <label>Vessel</label>
                <span className="value">
                  {trackingData.container?.vesselName || 'Not assigned'}
                </span>
              </div>
              
              <div className="summary-item">
                <label>Voyage</label>
                <span className="value">
                  {trackingData.container?.voyageNumber || 'N/A'}
                </span>
              </div>
              
              <div className="summary-item">
                <label>Port of Loading</label>
                <span className="value">
                  {trackingData.container?.portOfLoading || 'N/A'}
                </span>
              </div>
              
              <div className="summary-item">
                <label>Port of Discharge</label>
                <span className="value">
                  {trackingData.container?.portOfDischarge || 'N/A'}
                </span>
              </div>
            </div>
          </div>

          <div className="timeline-section">
            <h3>Shipment Timeline</h3>
            <TrackingTimeline timeline={trackingData.timeline} />
          </div>
        </div>
      )}

    </div>
  );
};

export default CustomerPortal;